/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useBoolean, usePositioner, useFormControlContext, safeInvoke, useKeyPress, useMergedRef } from '@sravni/react-utils';
import { isValid } from 'date-fns';
import { Z_INDEXES } from '../../../constants/zIndexes.js';
import '../../Portal/index.js';
import '../../TextInput/index.js';
import '../../Transition/index.js';
import '../../Calendar/index.js';
import { getDateFormatted } from '../../Calendar/utils/date.js';
import modules_640a77b2 from '../styles/DateInput.module.less.js';
import Calendar from '../../Calendar/src/Calendar.js';
import Portal from '../../Portal/src/Portal.js';
import { Fade } from '../../Transition/src/Fade.js';
import TextInput from '../../TextInput/src/TextInput.js';

function isDate(value) {
    return isValid(value);
}
var convertToRange = function (value) {
    if (isDate(value)) {
        return { from: value, to: value };
    }
    if (value && value.from && value.to) {
        return value;
    }
    return undefined;
};
/**
 * @description To select or input a date
 *
 * @component
 * @example
 * ```jsx
 * <DateInput />
 * ```
 */
var DateInput = React.forwardRef(function (props, ref) {
    var defaultValue = props.defaultValue, value = props.value, autoFocus = props.autoFocus, mode = props.mode, defaultDate = props.defaultDate, maxDate = props.maxDate, minDate = props.minDate, disabledIntervals = props.disabledIntervals, rangeLimit = props.rangeLimit, dateFormat = props.dateFormat, invalid = props.invalid, disabled = props.disabled, readOnly = props.readOnly, placement = props.placement, className = props.className, onClick = props.onClick, onChange = props.onChange, onFocus = props.onFocus, onBlur = props.onBlur, restProps = __rest(props, ["defaultValue", "value", "autoFocus", "mode", "defaultDate", "maxDate", "minDate", "disabledIntervals", "rangeLimit", "dateFormat", "invalid", "disabled", "readOnly", "placement", "className", "onClick", "onChange", "onFocus", "onBlur"]);
    var cx = useStyles(modules_640a77b2);
    var classNames = cx(className, 'container');
    var calendarWrapper = cx('calendarWrapper');
    var reference = React.useRef(null);
    var inputRef = React.useRef(null);
    var calendarRef = React.useRef(null);
    var popper = React.useRef(null);
    var isFirstRender = React.useRef(true);
    var _a = __read(useBoolean(false), 2), visible = _a[0], setVisible = _a[1];
    var _b = __read(useBoolean(false), 2), focused = _b[0], setFocused = _b[1];
    var _c = usePositioner({
        placement: placement,
        offset: [0, 8],
    }), referenceRef = _c.referenceRef, popperRef = _c.popperRef, getReferenceProps = _c.getReferenceProps, getPopperProps = _c.getPopperProps;
    var _d = __read(React.useState(convertToRange(value || defaultValue)), 2), innerValue = _d[0], setInnerValue = _d[1];
    var formControlContext = useFormControlContext();
    var isRange = mode === 'range';
    var displayValue = React.useMemo(function () {
        if (innerValue === undefined || innerValue === null) {
            return undefined;
        }
        var _a = innerValue, from = _a.from, to = _a.to;
        var dateFormats = Array.isArray(dateFormat) ? dateFormat : [dateFormat, dateFormat];
        if (isRange) {
            if (from && to) {
                return getDateFormatted(from, dateFormats[0]) + " \u2014 " + getDateFormatted(to, dateFormats[1]);
            }
            if (from) {
                return getDateFormatted(from);
            }
            return undefined;
        }
        return from && getDateFormatted(from, dateFormats[0]);
    }, [innerValue, dateFormat, isRange]);
    var handleChangeSelected = React.useCallback(function (range) {
        setInnerValue(range);
        safeInvoke(onChange, range);
        setVisible.off();
    }, [innerValue, onChange]);
    var handleClick = React.useCallback(function (event) {
        if (disabled)
            return;
        !readOnly && setVisible.toggle();
        safeInvoke(onClick, event);
    }, [readOnly, disabled, onClick]);
    var handleFocus = React.useCallback(function (event) {
        if (!focused) {
            setFocused.on();
            safeInvoke(onFocus, event);
        }
    }, [focused, onFocus]);
    var handleBlur = React.useCallback(function (event) {
        var _a;
        if (event.relatedTarget === calendarRef.current || ((_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget)))
            return;
        setVisible.off();
        safeInvoke(onBlur, event);
    }, [focused, onBlur]);
    var calendar = React.useMemo(function () { return (React.createElement(Calendar, { ref: calendarRef, mode: mode, maxDate: maxDate, minDate: minDate, rangeLimit: rangeLimit, disabledIntervals: disabledIntervals, selection: innerValue, defaultDate: defaultDate, tabIndex: -1, onChange: handleChangeSelected })); }, [defaultDate, mode, minDate, maxDate, rangeLimit, disabledIntervals, innerValue, handleChangeSelected]);
    var view = React.useMemo(function () {
        return (React.createElement(Portal, null,
            React.createElement(Fade, { visible: visible, unmountOnExit: true },
                React.createElement("div", __assign({ ref: popperRef, className: calendarWrapper }, getPopperProps({ style: { zIndex: Z_INDEXES.DROPDOWN } }, popper)), calendar))));
    }, [visible, calendar, calendarWrapper]);
    useKeyPress({
        enabled: visible || focused,
        keyMap: {
            Escape: function (event) {
                var _a;
                event.preventDefault();
                setVisible.off();
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            },
            Tab: function () {
                setVisible.off();
                setFocused.off();
            },
        },
    }, [visible, focused]);
    React.useEffect(function () {
        if (!isFirstRender.current) {
            setInnerValue(convertToRange(value));
        }
        else {
            isFirstRender.current = false;
        }
    }, [value]);
    React.useEffect(function () {
        autoFocus && inputRef.current && inputRef.current.focus();
    }, [autoFocus]);
    React.useEffect(function () {
        var listener = function (event) {
            if (reference.current && reference.current.contains(event.target)) {
                return;
            }
            if (popper.current && popper.current.contains(event.target)) {
                return;
            }
            setVisible.off();
            setFocused.off();
        };
        document.addEventListener('mousedown', listener);
        return function () {
            document.removeEventListener('mousedown', listener);
        };
    }, [reference, popper]);
    return (React.createElement("div", { "data-qa": "DateInput", className: classNames },
        React.createElement("div", __assign({ ref: referenceRef }, getReferenceProps({}, reference), { onClick: handleClick }),
            React.createElement(TextInput, __assign({}, restProps, { ref: useMergedRef(ref, inputRef), value: displayValue, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, readOnly: true, onFocus: handleFocus, onBlur: handleBlur }))),
        view));
});
DateInput.defaultProps = {
    mode: 'single',
    placement: 'auto-start',
};
DateInput.displayName = 'DateInput';

export default DateInput;
//# sourceMappingURL=DateInput.js.map
